import { Component, ViewEncapsulation } from "@angular/core";
import { ActivatedRoute } from "@angular/router";
import { Department } from "@enums/department.enum";
import { ProductionStatus } from "@enums/productionStatus.enum";
import { GetProductionStatusColor } from "@helpers/getProductionStatusColor";
import { GetPropertiesFromEnum } from "@helpers/getPropertiesFromEnum";
import { MachineService } from "@services/machine.service";
import { ToastService } from "angular-toastify";

@Component({
	encapsulation: ViewEncapsulation.None,
	moduleId: module.id,
	selector: "departmentMachineOverview",
	styleUrls: ["departmentMachineOverview.css"],
	templateUrl: "departmentMachineOverview.html"
})
export class DepartmentMachineOverviewComponent {
	breakdownCount = 0;
	breakdownMachines: any[] = [];
	department = 0;
	departmentName = "";
	lastRefresh = "";
	machines: any[] = [];
	machinesAllJobs: any[] = [];
	machinesNoJob: any[] = [];
	machinesRunning: any[] = [];
	pageIndex = 0;
	pageSize = 2;
	pagedMachines: any[] = [];
	private rotationTimer: any;
	site = 0;
	statusLookup: any[] = [];
	superRequiredCount = 0;
	supervisorMachines: any[] = [];
	totalPages = 0;
	
	constructor(private route: ActivatedRoute, private toastService: ToastService, private machineService: MachineService) {
		this.statusLookup = GetPropertiesFromEnum(ProductionStatus);
		const deptLookup = GetPropertiesFromEnum(Department);

		this.route.params.subscribe((params) => {
			this.department = params.department;
			this.site = params.site;
			const deptEntry = deptLookup.find(x => x.id == this.department);
			this.departmentName = deptEntry ? deptEntry.name : `Department ${this.department}`;
			this.getData();
		});

		setInterval(() => {
			this.getData();
		}, 5000);
	}

	getData() {
		this.machineService.app_OpsPlannerForDepartment(this.department)
			.subscribe(
				(res: any) => {
					this.machines = res.response.filter((x: any) => x.machineId !== null && x.siteName == "Kings Norton");
					this.machinesRunning = this.machines.filter(x => x.runningStages.length > 0);
					this.machinesNoJob = this.machines.filter(x => x.runningStages.length == 0);
					this.machinesAllJobs = this.machines;
					this.refreshPaging();

					const tempSuperRequiredCount = this.superRequiredCount;
					this.supervisorMachines = this.machines.filter(x => x.productionStatus == ProductionStatus.AssistanceRequired);
					this.superRequiredCount = this.supervisorMachines.length;
					if (this.superRequiredCount > tempSuperRequiredCount) {
						// this.playSupervisorAudio();
					}

					const tempBreakdownCount = this.breakdownCount;
					this.breakdownMachines = this.machines.filter(x => x.productionStatus == ProductionStatus.Breakdown);
					this.breakdownCount = this.breakdownMachines.length;
					if (this.breakdownCount > tempBreakdownCount) {
						// this.playBreakdownAudio();
					}

					this.lastRefresh = new Date().toLocaleTimeString([], { hour: "2-digit", minute: "2-digit" });
				},
				(err) => {
					console.log(err);
					this.toastService.error("Something Went Wrong!");
				}
			);
	}

	getNextJobs(machine: any): any[] {
		if (!machine.availableStages || machine.availableStages.length === 0) return [];
		return machine.availableStages.slice(0, 3);
	}

	getNextJobsPadded(machine: any): any[] {
		const jobs = this.getNextJobs(machine);
		const padded = [...jobs];
		while (padded.length < 3) {
			padded.push(null);
		}
		return padded;
	}

	getProgressWidth(stage: any): string {
		if (!stage || !stage.quantityRequired || stage.quantityRequired === 0) return "0%";
		const pct = (stage.quantityCompleted / stage.quantityRequired) * 100;
		return Math.min(Math.max(pct, 0), 100) + "%";
	}


	ngOnDestroy(): void {
		if (this.rotationTimer) {
			clearInterval(this.rotationTimer);
		}
	}

	playBreakdownAudio() {
		const audio = new Audio();
		audio.src = "../../../../../assets/machineBreakdown.mp3";
		audio.load();
		audio.autoplay = true;
		audio.play();
	}

	playSupervisorAudio() {
		const audio = new Audio();
		audio.src = "../../../../../assets/supervisorRequired.mp3";
		audio.load();
		audio.autoplay = true;
		audio.play();
	}


	private refreshPaging(): void {
		const total = this.machinesAllJobs?.length ?? 0;

		// If nothing to show
		if (total === 0) {
			this.pagedMachines = [];
			this.totalPages = 0;
			this.stopRotation();
			this.pageIndex = 0;
			return;
		}

		// Clamp pageIndex in case list shrank
		const pageCount = Math.ceil(total / this.pageSize);
		this.totalPages = pageCount;
		if (this.pageIndex >= pageCount) this.pageIndex = 0;

		// Slice current page
		const start = this.pageIndex * this.pageSize;
		this.pagedMachines = this.machinesAllJobs.slice(start, start + this.pageSize);

		// Start/stop rotation depending on whether paging is needed
		if (total > this.pageSize) {
			this.startRotation(pageCount);
		} else {
			this.stopRotation();
		}
	}


	renderStatusColor(status: ProductionStatus) {
		switch (status) {
			case ProductionStatus.Active:
			case ProductionStatus.Changeover:
				return "#22c55e";
			case ProductionStatus.ToiletBreak:
			case ProductionStatus.Break:
			case ProductionStatus.QLoop:
			case ProductionStatus.QualityCheck:
			case ProductionStatus.AssistanceRequired:
				return "#fb923c";
			case ProductionStatus.Idle:
			case ProductionStatus.Inactive:
			case ProductionStatus.InitialCheckFailed:
			case ProductionStatus.Breakdown:
			case ProductionStatus.Maintenance:
				return "red";
			default:
				return "blue";
		}
		return GetProductionStatusColor(status);
	}

	renderStatusName(status: number) {
		if (status == 100) {
			return "SUPERVISOR";
		}
		const entry = this.statusLookup.filter(x => x.id == status);
		return entry.length > 0 ? entry[0].name.toUpperCase() : "UNKNOWN";
	}

	private startRotation(pageCount: number): void {
		if (this.rotationTimer) return; // already running

		this.rotationTimer = setInterval(() => {
			this.pageIndex = (this.pageIndex + 1) % pageCount;

			const start = this.pageIndex * this.pageSize;
			this.pagedMachines = this.machinesAllJobs.slice(start, start + this.pageSize);
		}, 5000);
	}

	private stopRotation(): void {
		if (this.rotationTimer) {
			clearInterval(this.rotationTimer);
			this.rotationTimer = null;
		}
	}
}
